import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faSyncAlt, faCheckCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import evaApi from "../../services/api/evaApi";
import useAppSession from "../../stores/useAppSession";
import useWidget from "../../stores/useWidget";
import { toast } from "react-toastify";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 40px 24px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;
`;

const MainCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 32px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 24px 0 16px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 32px;
  margin-top: 32px;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

const ColorItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;

  label {
    font-size: 13px;
    font-weight: 500;
    color: #424245;
  }

  input[type="color"] {
    border: none;
    width: 24px;
    height: 24px;
    cursor: pointer;
    background: none;
    padding: 0;
    
    &::-webkit-color-swatch-wrapper { padding: 0; }
    &::-webkit-color-swatch {
      border-radius: 4px;
      border: 1px solid #eaeaea;
    }
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 15px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  outline: none;
  margin-top: 8px;

  &:focus {
    border-color: #ff8f40;
  }
`;

const ImagePicker = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const ImageOption = styled.div`
  border: 2px solid ${props => props.active ? "#ff8f40" : "#eaeaea"};
  border-radius: 6px;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s;

  img {
    width: 50px;
    height: 50px;
    display: block;
    border-radius: 4px;
  }
`;

const CodeBlock = styled.div`
  background: #1d1d1f;
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  
  pre {
    margin: 0;
    color: #f5f5f7;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 13px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.5;
  }
`;

const WidgetPreview = styled.div`
  width: 100%;
  max-width: 350px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  overflow: hidden;
  background: #ffffff;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 400px;
  position: relative;
`;

const PreviewHeader = styled.div`
  background: ${props => props.bg};
  color: ${props => props.text};
  padding: 16px;
  font-weight: bold;
  font-size: 15px;
`;

const PreviewBody = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9f9f9;
`;

const Bubble = styled.div`
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
  max-width: 80%;
  background: ${props => props.bg};
  color: ${props => props.text};
  align-self: ${props => props.user ? "flex-end" : "flex-start"};
`;

const PreviewButton = styled.img`
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const ActionButton = styled.button`
  background: ${props => props.secondary ? "#ffffff" : "#ff8f40"};
  color: ${props => props.secondary ? "#333" : "#ffffff"};
  border: 1px solid ${props => props.secondary ? "#eaeaea" : "#ff8f40"};
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  font-size: 15px;
  color: #666;
`;

const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const decodeToken = (token) => {
  try { return jwtDecode(token); } catch { return null; }
};

const Widget = () => {
  const [widgetUrl] = useState(window.location.origin + "/widget/chat-widget.js");
  const { token } = useAppSession();
  const decoded = decodeToken(token);
  const workspaceName = decoded?.user_access?.environment?.workspace?.name || "Workspace";

  const { colors: storedColors, loadWidgetFromAPI, setColors: setStoredColors } = useWidget();

  const [installToken, setInstallToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const [colors, setColors] = useState({
    header: "#ff8f40",
    headerText: "#ffffff",
    button: "#ff8f40",
    buttonText: "#ffffff",
    userBg: "#ff8f40",
    userText: "#ffffff",
    botBg: "#eaeaea",
    botText: "#333333",
    buttonImage: "bot_1.png",
  });

  const buttonImages = ["bot_1.png", "bot_2.png", "bot_3.png", "bot_4.png", "bot_5.png", "bot_6.png", "bot_7.png", "bot_8.png", "bot_9.png"];
  const isCustomImage = colors.buttonImage?.startsWith("http");

  const fetchToken = async () => {
    try {
      setLoading(true);
      const res = await evaApi.wtoken();
      if (res?.data?.wtoken) setInstallToken(res.data.wtoken);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToken();
    loadWidgetFromAPI();
  }, []);

  useEffect(() => {
    if (storedColors) setColors(storedColors);
  }, [storedColors]);

  const scriptCode = `<script 
  src="${widgetUrl}" 
  data-install-token="${installToken}"
  data-color-header="${colors.header}"
  data-color-header-text="${colors.headerText}"
  data-color-button="${colors.button}"
  data-color-button-text="${colors.buttonText}"
  data-color-user-bg="${colors.userBg}"
  data-color-user-text="${colors.userText}"
  data-color-bot-bg="${colors.botBg}"
  data-color-bot-text="${colors.botText}"
  data-button-image="${colors.buttonImage}"
  data-company-name="${capitalize(workspaceName)}"
  data-domain="${window.location.origin}"
></script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(scriptCode);
      setCopied(true);
      if (typeof toast.success === 'function') {
        toast.success("Código copiado com sucesso!");
      }
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const updateColor = (key, value) => {
    const updated = { ...colors, [key]: value };
    setColors(updated);
    setStoredColors(updated);
  };

  return (
    <PageWrapper>
      <MainCard>
        <Title>Guia de Instalação do Widget</Title>
        {loading ? (
          <Loader>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" />
            <p style={{ marginTop: "16px" }}>Buscando configurações...</p>
          </Loader>
        ) : (
          <>
            <GridContainer>
              <div>
                <SectionTitle>Personalização Visual</SectionTitle>
                <ColorGrid>
                  {[
                    { key: "header", label: "Cabeçalho" },
                    { key: "headerText", label: "Texto Cabeçalho" },
                    { key: "button", label: "Cor Botão" },
                    { key: "buttonText", label: "Texto Botão" },
                    { key: "userBg", label: "Fundo Usuário" },
                    { key: "userText", label: "Texto Usuário" },
                    { key: "botBg", label: "Fundo Bot" },
                    { key: "botText", label: "Texto Bot" },
                  ].map((item) => (
                    <ColorItem key={item.key}>
                      <label>{item.label}</label>
                      <input
                        type="color"
                        value={colors[item.key]}
                        onChange={(e) => updateColor(item.key, e.target.value)}
                      />
                    </ColorItem>
                  ))}
                </ColorGrid>
                <SectionTitle>Ícone do Chat</SectionTitle>
                <InputField
                  placeholder="URL da imagem personalizada (opcional)"
                  value={isCustomImage ? colors.buttonImage : ""}
                  onChange={(e) => updateColor("buttonImage", e.target.value)}
                />
                <ImagePicker>
                  {buttonImages.map((img) => (
                    <ImageOption
                      key={img}
                      active={colors.buttonImage === img}
                      onClick={() => updateColor("buttonImage", img)}
                    >
                      <img src={`/widget/images/${img}`} alt={img} />
                    </ImageOption>
                  ))}
                </ImagePicker>
              </div>
              <div>
                <SectionTitle>Pré-visualização</SectionTitle>
                <WidgetPreview>
                  <PreviewHeader bg={colors.header} text={colors.headerText}>
                    {workspaceName} Chat
                  </PreviewHeader>
                  <PreviewBody>
                    <Bubble bg={colors.botBg} text={colors.botText}>
                      Olá! Como posso ajudar hoje?
                    </Bubble>
                    <Bubble bg={colors.userBg} text={colors.userText} user>
                      Gostaria de testar as cores!
                    </Bubble>
                  </PreviewBody>
                  <PreviewButton
                    src={isCustomImage ? colors.buttonImage : `/widget/images/${colors.buttonImage}`}
                  />
                </WidgetPreview>
              </div>
            </GridContainer>
            <SectionTitle>Código de Integração</SectionTitle>
            <p style={{ fontSize: '15px', color: '#666' }}>
              Copie o código abaixo e cole antes do fechamento da tag body no seu site.
            </p>
            <CodeBlock>
              <pre>{scriptCode}</pre>
            </CodeBlock>
            <ButtonGroup>
              <ActionButton onClick={handleCopy}>
                <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} />
                {copied ? "Copiado!" : "Copiar Código"}
              </ActionButton>
              <ActionButton secondary onClick={fetchToken}>
                <FontAwesomeIcon icon={faSyncAlt} />
                Atualizar Token
              </ActionButton>
            </ButtonGroup>
          </>
        )}
      </MainCard>
    </PageWrapper>
  );
};

export default Widget;